import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { logout, useGetProfile } from '@sunrise-ui/api-client';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  Header,
  HeaderButton,
  HeaderTitle,
  LogoIcon,
  ProgressBar,
  TextReg,
  useAuthStore,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { AcceptTermsModalScreen } from './AcceptTermsModalScreen';
import { ProfileSetupModalScreen } from './ProfileSetupModalScreen';

/**
 * Onboarding modal content (profile setup and terms acceptance).
 * Used inside AuthFlowModal parent component.
 */
export function OnboardingModalContent() {
  const { currentUserId, hasAcceptedTerms, modalFlow, dismissModalFlow } =
    useAuthStore();
  const { isDismissible } = modalFlow;
  const { data: profile } = useGetProfile(currentUserId);
  const hasFilledOutProfile = Boolean(
    profile?.first_name && profile?.last_name && profile?.headline,
  );
  const styles = useDynamicStyles(createStyles);

  const handleHide = () => {
    if (isDismissible) dismissModalFlow();
  };

  const logoutButton = (
    <TouchableOpacity onPress={logout} style={styles.logoutButton}>
      <TextReg style={styles.logoutText}>Log out</TextReg>
    </TouchableOpacity>
  );

  return (
    <>
      <Header
        headerTitle={
          <View style={styles.headerLogoContainer}>
            <HeaderTitle logo={LogoIcon} />
          </View>
        }
        headerRight={
          isDismissible ? (
            <HeaderButton icon={Cancel01Icon} onPress={handleHide} />
          ) : undefined
        }
        headerLeft={logoutButton}
      />
      <View style={styles.content}>
        <ProgressBar currentStep={3} totalSteps={3} />
        {hasFilledOutProfile ? (
          hasAcceptedTerms ? undefined : (
            <AcceptTermsModalScreen />
          )
        ) : (
          <ProfileSetupModalScreen />
        )}
      </View>
    </>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    content: {
      flex: 1,
      padding: isMobile ? 12 : 24,
      alignItems: 'center',
    },
    headerLogoContainer: {
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 40,
      minWidth: 100,
    },
    logoutButton: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 4.5,
    },
    logoutText: {
      fontSize: 14,
    },
  });
